import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import ReactMarkdown from 'react-markdown';
import { getProjectById, getProjects } from '../services/markdownProjectService';

const CaseStudyContainer = styled.div`
  padding: var(--spacing-xxl) 0;
`;

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 var(--spacing-lg);
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  color: var(--text-light);
  text-decoration: none;
  margin-bottom: var(--spacing-lg);
  
  &:hover {
    color: var(--accent-lavender);
  }
  
  &::before {
    content: '←';
    margin-right: 0.5rem;
  }
`;

const HeroSection = styled.div`
  margin-bottom: var(--spacing-xl);
`;

const CategoryTag = styled.span`
  display: inline-block;
  background-color: ${props => {
    switch(props.category) {
      case 'Travel Marketing': 
        return 'rgba(62, 221, 226, 0.1)';
      case 'Tourism Development': 
        return 'rgba(123, 123, 243, 0.1)';
      case 'Digital Tools': 
        return 'rgba(123, 211, 244, 0.1)';
      default: 
        return 'rgba(62, 221, 226, 0.1)';
    }
  }};
  color: ${props => {
    switch(props.category) {
      case 'Travel Marketing': 
        return 'var(--accent-turquoise)';
      case 'Tourism Development': 
        return 'var(--accent-lavender)';
      case 'Digital Tools': 
        return 'var(--accent-blue)';
      default: 
        return 'var(--accent-turquoise)';
    }
  }};
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: var(--spacing-md);
`;

const PageTitle = styled.h1`
  margin-bottom: var(--spacing-sm);
  
  span {
    color: var(--accent-lavender);
  }
`;

const KeyStat = styled.div`
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--accent-lavender);
  margin-bottom: var(--spacing-lg);
`;

const FeaturedImage = styled.div`
  width: 100%;
  height: 400px;
  border-radius: 8px;
  background-size: cover;
  background-position: center;
  margin-bottom: var(--spacing-xl);
`;

const ContentSection = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: var(--spacing-xl);
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const MainContent = styled.div`
  font-size: 1.1rem;
  line-height: 1.7;
  
  h2 {
    margin-top: var(--spacing-xl);
    margin-bottom: var(--spacing-md);
    font-size: 1.75rem;
  }
  
  p {
    margin-bottom: var(--spacing-md);
  }
  
  ul, ol {
    margin-bottom: var(--spacing-md);
    padding-left: var(--spacing-lg);
  }
  
  li {
    margin-bottom: var(--spacing-xs);
  }
`;

const Sidebar = styled.div`
  @media (max-width: 768px) {
    margin-top: var(--spacing-xl);
  }
`;

const SidebarSection = styled.div`
  background-color: var(--secondary);
  padding: var(--spacing-lg);
  border-radius: 8px;
  margin-bottom: var(--spacing-lg);
`;

const SidebarTitle = styled.h3`
  font-size: 1.2rem;
  margin-bottom: var(--spacing-md);
`;

const ResultsList = styled.ul`
  list-style: none;
  padding: 0;
`;

const ResultItem = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: var(--spacing-sm);
  
  &::before {
    content: '✓';
    color: var(--accent-turquoise);
    margin-right: var(--spacing-sm);
    font-weight: bold;
  }
`;

const RelatedCaseStudiesSection = styled.div`
  margin-top: var(--spacing-xxl);
`;

const RelatedSectionTitle = styled.h2`
  text-align: center;
  margin-bottom: var(--spacing-xl);
  
  span {
    color: var(--accent-lavender);
  }
`;

const RelatedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--spacing-lg);
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const CTASection = styled.div`
  background-color: var(--secondary);
  padding: var(--spacing-xl);
  border-radius: 8px;
  text-align: center;
  margin-top: var(--spacing-xxl);
`;

const CTATitle = styled.h2`
  margin-bottom: var(--spacing-md);
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background-color: var(--accent-turquoise);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  text-decoration: none;
  border: 2px solid var(--accent-turquoise);
  transition: all 0.3s ease;
  margin-top: var(--spacing-md);
  
  &:hover {
    background-color: transparent;
    color: var(--text);
  }
`;

const RelatedCard = styled(Link)`
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  text-decoration: none;
  display: block;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
`;

const RelatedImage = styled.div`
  height: 160px;
  background-size: cover;
  background-position: center;
`;

const RelatedContent = styled.div`
  padding: var(--spacing-md);
`;

const RelatedCategory = styled.span`
  display: inline-block;
  background-color: ${props => {
    switch(props.category) {
      case 'Travel Marketing': 
        return 'rgba(62, 221, 226, 0.1)';
      case 'Tourism Development': 
        return 'rgba(123, 123, 243, 0.1)';
      case 'Digital Tools': 
        return 'rgba(123, 211, 244, 0.1)';
      default: 
        return 'rgba(62, 221, 226, 0.1)';
    }
  }};
  color: ${props => {
    switch(props.category) {
      case 'Travel Marketing': 
        return 'var(--accent-turquoise)';
      case 'Tourism Development': 
        return 'var(--accent-lavender)';
      case 'Digital Tools': 
        return 'var(--accent-blue)';
      default: 
        return 'var(--accent-turquoise)';
    }
  }};
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  margin-bottom: var(--spacing-xs);
`;

const RelatedTitle = styled.h3`
  font-size: 1.1rem;
  margin: var(--spacing-xs) 0;
  color: var(--text);
`;

const MarkdownContent = styled.div`
  font-size: 1.1rem;
  line-height: 1.7;
  
  h1, h2, h3, h4, h5, h6 {
    margin-top: var(--spacing-xl);
    margin-bottom: var(--spacing-md);
    font-family: 'Montserrat', sans-serif;
    font-weight: 700;
  }
  
  h1 {
    font-size: 2.25rem;
    color: var(--text);
  }
  
  h2 {
    font-size: 1.75rem;
    color: var(--text);
    border-bottom: 2px solid var(--accent-turquoise);
    padding-bottom: var(--spacing-xs);
    margin-top: var(--spacing-xxl);
  }
  
  h3 {
    font-size: 1.5rem;
    color: var(--accent-lavender);
    margin-top: var(--spacing-xl);
  }
  
  h4 {
    font-size: 1.25rem;
    color: var(--text);
    margin-top: var(--spacing-lg);
  }
  
  p {
    margin-bottom: var(--spacing-md);
    color: var(--text);
  }
  
  ul, ol {
    margin-bottom: var(--spacing-md);
    padding-left: var(--spacing-lg);
  }
  
  li {
    margin-bottom: var(--spacing-xs);
    color: var(--text);
  }
  
  strong {
    font-weight: 600;
    color: var(--text);
  }
  
  blockquote {
    margin: var(--spacing-lg) 0;
    padding: var(--spacing-md) var(--spacing-lg);
    border-left: 4px solid var(--accent-turquoise);
    background-color: rgba(62, 221, 226, 0.05);
    font-style: italic;
  }
  
  code {
    background-color: var(--secondary);
    padding: 0.2rem 0.4rem;
    border-radius: 4px;
    font-family: monospace;
  }
  
  pre {
    background-color: var(--secondary);
    padding: var(--spacing-md);
    border-radius: 8px;
    overflow-x: auto;
    margin: var(--spacing-md) 0;
  }
`;

const ExecutiveSummary = styled.div`
  background-color: var(--secondary);
  padding: var(--spacing-lg);
  border-radius: 8px;
  margin-bottom: var(--spacing-xl);
  border-left: 4px solid var(--accent-turquoise);
  
  h3 {
    color: var(--accent-turquoise);
    margin-top: 0;
    margin-bottom: var(--spacing-md);
  }
  
  p {
    margin-bottom: 0;
    font-size: 1.1rem;
    line-height: 1.6;
  }
`;

const CaseStudyDetail = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [relatedProjects, setRelatedProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchProject = async () => {
      try {
        setLoading(true);
        
        // Get the project by ID or slug
        const projectData = await getProjectById(id);
        
        if (projectData) {
          setProject(projectData);
          
          // Get related projects from the same category
          const allProjects = await getProjects();
          const related = allProjects
            .filter(p => p.category === projectData.category && p.id !== projectData.id)
            .slice(0, 3)
            .map(p => ({
              id: p.id,
              title: p.title,
              category: mapCategoryToDisplay(p.category),
              image: p.imageUrl
            }));
          
          setRelatedProjects(related);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching project:', error);
        setLoading(false);
      }
    };
    
    fetchProject();
  }, [id]);
  
  // Map category IDs to display names
  const mapCategoryToDisplay = (category) => {
    const categoryMap = {
      'travel-marketing': 'Travel Marketing',
      'tourism-development': 'Tourism Development',
      'digital-tools': 'Digital Tools'
    };
    return categoryMap[category] || category;
  };
  
  if (loading) {
    return (
      <CaseStudyContainer>
        <Container>
          <p>Loading case study...</p>
        </Container>
      </CaseStudyContainer>
    );
  }
  
  if (!project) {
    return (
      <CaseStudyContainer>
        <Container>
          <p>Case study not found.</p>
          <BackLink to="/projects">Back to Projects</BackLink>
        </Container>
      </CaseStudyContainer>
    );
  }
  
  const categoryDisplay = mapCategoryToDisplay(project.category);
  
  return (
    <CaseStudyContainer>
      <Container>
        <BackLink to="/projects">Back to Projects</BackLink>
        
        <HeroSection>
          <CategoryTag category={categoryDisplay}>{categoryDisplay}</CategoryTag>
          <PageTitle>{project.title}</PageTitle>
          <KeyStat>{project.summary}</KeyStat>
        </HeroSection>
        
        <FeaturedImage style={{ backgroundImage: `url(${project.imageUrl})` }} />
        
        <ContentSection>
          <MainContent>
            {project.summary && (
              <ExecutiveSummary>
                <h3>Executive Summary</h3>
                <p>{project.summary}</p>
              </ExecutiveSummary>
            )}
            
            <MarkdownContent>
              <ReactMarkdown>{project.content}</ReactMarkdown>
            </MarkdownContent>
          </MainContent>
          
          <Sidebar>
            {project.frontmatter?.date && (
              <SidebarSection>
                <SidebarTitle>Project Timeline</SidebarTitle>
                <p>{new Date(project.frontmatter.date).getFullYear()}</p>
              </SidebarSection>
            )}
            
            {project.frontmatter?.client && (
              <SidebarSection>
                <SidebarTitle>Client</SidebarTitle>
                <p>{project.frontmatter.client}</p>
              </SidebarSection>
            )}
            
            {project.frontmatter?.location && (
              <SidebarSection>
                <SidebarTitle>Location</SidebarTitle>
                <p>{project.frontmatter.location}</p>
              </SidebarSection>
            )}
            
            <SidebarSection>
              <SidebarTitle>Category</SidebarTitle>
              <p>{categoryDisplay}</p>
            </SidebarSection>
            
            {project.frontmatter?.key_findings && (
              <SidebarSection>
                <SidebarTitle>Key Findings</SidebarTitle>
                <ul style={{ listStyle: 'none', padding: 0 }}>
                  {project.frontmatter.key_findings.map((finding, index) => (
                    <li key={index} style={{ marginBottom: 'var(--spacing-sm)' }}>
                      <strong>{finding.title}:</strong> {finding.description}
                    </li>
                  ))}
                </ul>
              </SidebarSection>
            )}
            
            <SidebarSection>
              <SidebarTitle>Interested in similar results?</SidebarTitle>
              <p>Let's discuss how we can apply these strategies to your travel business.</p>
              <CTAButton to="/contact">Contact Us</CTAButton>
            </SidebarSection>
          </Sidebar>
        </ContentSection>
        
        {relatedProjects.length > 0 && (
          <RelatedCaseStudiesSection>
            <RelatedSectionTitle>Related <span>Case Studies</span></RelatedSectionTitle>
            <RelatedGrid>
              {relatedProjects.map(related => (
                <RelatedCard key={related.id} to={`/projects/${related.id}`}>
                  <RelatedImage style={{ backgroundImage: `url(${related.image})` }} />
                  <RelatedContent>
                    <RelatedCategory category={related.category}>{related.category}</RelatedCategory>
                    <RelatedTitle>{related.title}</RelatedTitle>
                  </RelatedContent>
                </RelatedCard>
              ))}
            </RelatedGrid>
          </RelatedCaseStudiesSection>
        )}
        
        <CTASection>
          <CTATitle>Ready to Experiment?</CTATitle>
          <p>Let's discuss how our team can help your travel business achieve similar results.</p>
          <CTAButton to="/contact">Start Your Project</CTAButton>
        </CTASection>
      </Container>
    </CaseStudyContainer>
  );
};

export default CaseStudyDetail; 